import { inject, Injectable, Injector } from "@angular/core";
import { CognitoService } from "@services/cognito/cognito.service";
import { SystemService } from "@services/system/system.service";
import { SharedService } from "@services/shared/shared.service";
import { invokeWithBackendLog } from './backend-log.interceptor';



@Injectable({
  providedIn: "root",
})
export class AuthorizationInterceptor {
  public cognitoService = inject(CognitoService);
  private system = inject(SystemService);
  private sharedService = inject(SharedService);
  private injector = inject(Injector);

  /**
   * Acima deste tempo, uma falha "Unknown Content-type received." deixa de ser
   * tratada como 401: rejeição de token volta imediatamente, enquanto 504 de
   * gateway só responde depois de esgotar o timeout da borda.
   */
  private static readonly AUTH_ERROR_MAX_ELAPSED_MS = 5000;

  // Static flags — shared across ALL instances so that even services using
  // `new AuthorizationInterceptor()` coordinate refresh correctly.
  private static isRefreshing = false;
  private static refreshFailed = false;
  private static refreshPromise: Promise<boolean> | null = null;

  constructor() {}

  public resetState(): void {
    AuthorizationInterceptor.isRefreshing = false;
    AuthorizationInterceptor.refreshFailed = false;
    AuthorizationInterceptor.refreshPromise = null;
  }

  intercept = async (request: any, invoker: any) => {
    const invoke = (attempt: number) => invokeWithBackendLog(request, invoker, attempt);
    const isAuthRequest = this.isAuthenticationRequest(request);

    // Auth requests (login, signup, refresh, etc.) skip token logic entirely
    if (isAuthRequest) {
      if (AuthorizationInterceptor.refreshFailed) {
        this.resetState();
      }
      return invoke(1);
    }

    // Attach current access token
    const authorization = this.getGrpcAuthorization();
    if (authorization) {
      request.getMetadata()["Authorization"] = authorization;
    }

    const startedAt = Date.now();
    try {
      return await invoke(1);
    } catch (error: any) {
      // Falha de borda (504/502/503, conexão caída, deadline) não se resolve com
      // refresh: repetir só dobraria a espera do usuário e poderia gerar uma
      // segunda cobrança. Marca o erro para a UI dar o tratamento certo.
      if (this.isGatewayFailure(error, Date.now() - startedAt)) {
        error.isGatewayFailure = true;
        throw error;
      }

      // Only attempt refresh for auth errors (401 / UNAUTHENTICATED)
      const methodName = String(request?.getMethodDescriptor?.()?.getName?.() || '');
      const isCampaignAuthError = (error.code === 7 || error.code === 403)
        && /\/(GetMyOffers|GetMyBenefits)$/.test(methodName);
      if ((!this.isAuthenticationError(error) && !isCampaignAuthError) || AuthorizationInterceptor.refreshFailed) {
        throw error;
      }

      const hasRefreshToken = !!(this.sharedService.refreshToken || this.sharedService.cookieService.get("refreshToken"));
      if (!hasRefreshToken) {
        throw error;
      }
      const renewed = await this.tryRenewToken();
      if (!renewed) {
        throw error;
      }

      // Refresh succeeded — rebuild metadata with new token and retry once
      const newAuth = this.getGrpcAuthorization();
      // Create a fresh metadata map to avoid stale references
      const metadata = request.getMetadata();
      if (newAuth) {
        metadata["Authorization"] = newAuth;
      }

      const retriedAt = Date.now();
      try {
        return await invoke(2);
      } catch (retryError: any) {
        if (this.isGatewayFailure(retryError, Date.now() - retriedAt)) {
          retryError.isGatewayFailure = true;
        }
        throw retryError;
      }
    }
  };

  private isAuthenticationRequest(request: any): boolean {
    // gRPC-web Request objects expose the method via getMethodDescriptor().getName()
    // which returns strings like "/auth.v1.AuthService/RefreshToken"
    let methodName = "";
    try {
      methodName = String(
        request?.getMethodDescriptor?.()?.getName?.() || "",
      ).toLowerCase();
    } catch {
      // Fallback: try legacy property access
      methodName = String(
        request?.method?.path || request?.method?.name || request?.methodPath || "",
      ).toLowerCase();
    }

    const requestType = String(request?.constructor?.name || "").toLowerCase();

    const authMethods = [
      "login", "signup", "loginoauth", "validatecodeoauth", "refreshtoken",
      "forgotmypassword", "resetmypassword", "loginpasswordless", "loginotp",
    ];

    return authMethods.some(
      (m) => methodName.includes(m) || requestType.includes(m),
    );
  }

  /**
   * Detects authentication errors:
   * - gRPC code 16 = UNAUTHENTICATED (standard)
   * - gRPC code 2 + "Unknown Content-type" = HTTP 401 via gRPC-web proxy
   */
  private isAuthenticationError(error: any): boolean {
    if (error.code === 16) return true;
    if (this.isUnparseableResponse(error)) return true;
    return false;
  }

  /**
   * Detecta falha vinda da borda em vez do serviço.
   *
   * gRPC code 4 = DEADLINE_EXCEEDED, 14 = UNAVAILABLE.
   *
   * O caso ambíguo é o code 2: quando o proxy responde 504/502/503, o corpo vem
   * em text/plain e o grpc-web produz exatamente o mesmo erro de um 401
   * atravessando o proxy. O que separa os dois é o tempo — por isso a decisão
   * usa o tempo decorrido da chamada.
   */
  private isGatewayFailure(error: any, elapsedMs: number): boolean {
    if (error?.code === 4 || error?.code === 14) return true;
    return (
      this.isUnparseableResponse(error) &&
      elapsedMs >= AuthorizationInterceptor.AUTH_ERROR_MAX_ELAPSED_MS
    );
  }

  /** Resposta que o grpc-web não conseguiu interpretar (proxy devolveu não-gRPC). */
  private isUnparseableResponse(error: any): boolean {
    return (
      error?.code === 2 &&
      typeof error.message === "string" &&
      error.message.toLowerCase().includes("unknown content-type")
    );
  }

  private getGrpcAuthorization(): string | null {
    if (this.system.AUTH_PROVIDER === "cognito") {
      const token = localStorage.getItem("accessToken");
      return token ? `Bearer ${token}` : null;
    }
    let accessToken = this.sharedService.accessToken;
    if (!accessToken) {
      accessToken = this.sharedService.cookieService.get("accessToken") || null;
      if (accessToken) this.sharedService.accessToken = accessToken;
    }
    return accessToken ? `Bearer ${accessToken}` : null;
  }

  private async tryRenewToken(): Promise<boolean> {
    // If another instance is already refreshing, piggyback on that promise
    if (AuthorizationInterceptor.isRefreshing && AuthorizationInterceptor.refreshPromise) {
      return AuthorizationInterceptor.refreshPromise;
    }

    AuthorizationInterceptor.isRefreshing = true;
    AuthorizationInterceptor.refreshPromise = this.executeRefresh();

    try {
      return await AuthorizationInterceptor.refreshPromise;
    } finally {
      AuthorizationInterceptor.isRefreshing = false;
      AuthorizationInterceptor.refreshPromise = null;
    }
  }

  private async executeRefresh(): Promise<boolean> {
    try {
      if (this.system.AUTH_PROVIDER === "cognito") {
        await this.cognitoService.refreshSession();
        AuthorizationInterceptor.refreshFailed = false;
        return true;
      }

      const refreshToken = this.sharedService.refreshToken || this.sharedService.cookieService.get("refreshToken");
      if (!refreshToken) {
        AuthorizationInterceptor.refreshFailed = true;
        return false;
      }

      const GrpcAuthService = require("@services/grpc-auth/grpc-auth.service").GrpcAuthService;
      const grpcAuthService = this.injector.get(GrpcAuthService);
      const response = await grpcAuthService.refreshToken();

      // Proto response — try .toObject() first, fallback to direct access
      const res = typeof response?.toObject === "function" ? response.toObject() : response;

      if (res?.tokens?.accessToken && res?.tokens?.refreshToken) {
        this.sharedService.accessToken = res.tokens.accessToken;
        this.sharedService.refreshToken = res.tokens.refreshToken;
        AuthorizationInterceptor.refreshFailed = false;
        return true;
      }
      AuthorizationInterceptor.refreshFailed = true;
      return false;
    } catch (error: unknown) {
      AuthorizationInterceptor.refreshFailed = true;
      return false;
    }
  }
}
